import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext, closestCenter, useSensor, useSensors,
  PointerSensor, TouchSensor, useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Shirt, Layers, Footprints, Watch, Package, RotateCcw, Sparkles, Bookmark, X, Plus, Pencil, Trash2,
  Glasses, Minus, WashingMachine, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi, photoUrl } from './api';
import { AddItemModal } from './AddItemModal';

const CATS = [
  { id: 'top', label: 'Tops', icon: Shirt },
  { id: 'bottom', label: 'Bottoms', icon: Package },
  { id: 'outerwear', label: 'Outerwear', icon: Layers },
  { id: 'shoes', label: 'Shoes', icon: Footprints },
  { id: 'watch', label: 'Watch', icon: Watch },
  { id: 'belt', label: 'Belt', icon: Minus },
  { id: 'sunglasses', label: 'Sunglasses', icon: Glasses },
  { id: 'other', label: 'Other', icon: Package },
];
const MULTI = new Set(['other']);

const COLOR_HEX = {
  white: '#F2F1EC', 'off-white': '#EDEAE2', black: '#141414', cream: '#EFE6D4',
  beige: '#D9C7A7', sand: '#D8C39A', stone: '#C9C2B4', tan: '#C8A175',
  navy: '#1F2A44', blue: '#3B82C4', 'light blue': '#9DB8D2', denim: '#4A6B8A',
  grey: '#8A8A8A', gray: '#8A8A8A', charcoal: '#3C3C3C', brown: '#6B4A2F',
  camel: '#B98A50', green: '#3F5C43', olive: '#6B6B3F', red: '#A33A33', burgundy: '#6E2B34',
};
const swatch = (item) => COLOR_HEX[(item.colors?.[0] || '').toLowerCase()] || '#5B6470';

const EditItemModal = ({ item, onClose, onReload }) => {
  const [name, setName] = useState(item.name);
  const [cat, setCat] = useState(item.category || 'other');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const v = name.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      const changes = {};
      if (v !== item.name) changes.name = v;
      if (cat !== (item.category || 'other')) changes.category = cat;
      if (Object.keys(changes).length) {
        await nexaApi.patch(`/wardrobe/${item.id}`, changes);
        toast.success(changes.name ? 'Saved — new picture on its way' : 'Saved');
        onReload();
      }
      onClose();
    } catch {
      toast.error('Could not rename');
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await nexaApi.delete(`/wardrobe/${item.id}`);
      toast.success('Removed from wardrobe');
      onReload();
      onClose();
    } catch {
      toast.error('Could not remove');
      setBusy(false);
    }
  };
  const toggleCleaning = async () => {
    if (busy) return;
    setBusy(true);
    const next = !item.at_cleaning;
    try {
      await nexaApi.patch(`/wardrobe/${item.id}`, { at_cleaning: next });
      toast.success(next ? 'Sent to cleaning' : 'Back from cleaning');
      onReload();
      onClose();
    } catch {
      toast.error('Could not update');
      setBusy(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="nexa-edit-item-modal"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-6">
        <div className="flex items-center gap-3 mb-5">
          {item.thumb_photo_id && (
            <img src={photoUrl(item.thumb_photo_id)} alt=""
              className="w-14 h-14 rounded-xl object-cover border border-white/10" />
          )}
          <div className="min-w-0">
            <h3 className="text-base font-light nexa-heading truncate">{item.name}</h3>
            {item.colors?.length > 0 && (
              <p className="text-[11px] text-white/40">{item.colors.join(' · ')}</p>
            )}
          </div>
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          data-testid="nexa-edit-item-name"
          className="nexa-input w-full h-11 rounded-xl px-4 text-sm mb-3"
        />
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="nexa-edit-item-cats">
          {CATS.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)}
              data-testid={`nexa-edit-cat-${c.id}`}
              className={`h-8 px-3 rounded-lg border text-[11px] font-medium transition-colors ${
                cat === c.id ? 'border-sky-400 text-sky-400 bg-sky-400/10' : 'border-white/15 text-white/50'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mb-2">
          <motion.button whileTap={{ scale: 0.96 }} onClick={save} disabled={busy || !name.trim()}
            data-testid="nexa-edit-item-save"
            className="flex-1 h-11 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-30">
            Save
          </motion.button>
          <button onClick={remove} disabled={busy} data-testid="nexa-edit-item-delete"
            aria-label="Delete item"
            className="w-11 h-11 rounded-xl border border-red-400/40 flex items-center justify-center text-red-400 active:scale-95 transition-transform">
            <Trash2 className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <button
          onClick={toggleCleaning}
          disabled={busy}
          data-testid="nexa-edit-item-cleaning"
          className={`w-full h-11 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium active:scale-95 transition-transform ${
            item.at_cleaning
              ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-300'
              : 'border-sky-400/40 bg-sky-400/[0.06] text-sky-300'
          }`}
        >
          {item.at_cleaning ? (
            <>
              <Check className="w-4 h-4" strokeWidth={2.5} />
              Back from cleaning
            </>
          ) : (
            <>
              <WashingMachine className="w-4 h-4" strokeWidth={1.8} />
              Send to cleaning
            </>
          )}
        </button>
      </motion.div>
    </motion.div>
  );
};

const ItemTile = ({ item, manage, laundry, laundryPicked, active, onTap }) => {
  const cleaning = !!item.at_cleaning;
  const disabled = cleaning && !manage && !laundry;
  return (
    <button
      onClick={() => (disabled ? null : onTap(item))}
      disabled={disabled}
      data-testid={`nexa-slot-${item.id}`}
      className={`relative w-full rounded-xl border p-1.5 text-left transition-all active:scale-95 ${
        laundry
          ? laundryPicked
            ? 'border-sky-400 bg-sky-400/15 shadow-[0_0_14px_rgba(56,189,248,0.35)]'
            : 'border-white/10 bg-white/[0.03]'
          : manage
            ? 'border-amber-400/40 bg-amber-400/[0.04]'
            : disabled
              ? 'border-white/10 bg-white/[0.02] cursor-not-allowed'
              : active
                ? 'border-sky-400 bg-sky-400/10 shadow-[0_0_14px_rgba(56,189,248,0.25)]'
                : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      {manage && !laundry && (
        <span className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black/80 border border-amber-400/50 flex items-center justify-center">
          <Pencil className="w-2.5 h-2.5 text-amber-400" strokeWidth={2.5} />
        </span>
      )}
      {laundry && laundryPicked && (
        <span className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-sky-400 flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />
        </span>
      )}
      {cleaning && !manage && !laundry && (
        <span
          data-testid={`nexa-slot-cleaning-${item.id}`}
          className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black/85 border border-white/20 flex items-center justify-center"
          title="At cleaning"
        >
          <WashingMachine className="w-2.5 h-2.5 text-white/70" strokeWidth={2} />
        </span>
      )}
      {cleaning && laundry && !laundryPicked && (
        <span className="absolute top-1 left-1 z-10 w-5 h-5 rounded-full bg-black/85 border border-white/20 flex items-center justify-center">
          <WashingMachine className="w-2.5 h-2.5 text-white/70" strokeWidth={2} />
        </span>
      )}
      {item.thumb_photo_id ? (
        <img
          src={photoUrl(item.thumb_photo_id)}
          alt=""
          loading="lazy"
          className={`w-full aspect-square object-cover rounded-lg mb-1.5 ${
            disabled ? 'grayscale opacity-40' : ''
          }`}
        />
      ) : (
        <span className={`w-full aspect-square rounded-lg mb-1.5 flex items-center justify-center bg-white/[0.04] ${
          disabled ? 'grayscale opacity-40' : ''
        }`}>
          <span className="block w-6 h-6 rounded-full border border-white/20"
            style={{ backgroundColor: swatch(item) }} />
        </span>
      )}
      <span className={`block px-1 pb-0.5 text-[11px] leading-tight line-clamp-2 ${
        disabled ? 'text-white/30' : (active && !manage) || (laundry && laundryPicked) ? 'text-sky-100' : 'text-white/70'
      }`}>
        {item.name}
      </span>
    </button>
  );
};

const SortableTile = ({ item, manage, laundry, laundryPicked, active, onTap }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, data: { cat: item.category || 'other' }, disabled: laundry });
  return (
    <div ref={setNodeRef} {...(laundry ? {} : attributes)} {...(laundry ? {} : listeners)}
      style={{
        transform: CSS.Transform.toString(transform), transition,
        zIndex: isDragging ? 30 : 'auto', opacity: isDragging ? 0.55 : 1,
        touchAction: 'manipulation',
      }}>
      <ItemTile item={item} manage={manage} laundry={laundry}
        laundryPicked={laundryPicked} active={active} onTap={onTap} />
    </div>
  );
};

const CatDropZone = ({ cat }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `cat:${cat}`, data: { cat } });
  return (
    <div ref={setNodeRef} data-testid={`nexa-cat-drop-${cat}`}
      className={`h-16 rounded-xl border border-dashed flex items-center justify-center text-[11px] transition-colors ${
        isOver ? 'border-sky-400 text-sky-300 bg-sky-400/10' : 'border-white/15 text-white/30'
      }`}>
      drag an item here
    </div>
  );
};

export const DressingRoom = ({ items, onReload }) => {
  const [basePhoto, setBasePhoto] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [selected, setSelected] = useState({});
  const [tucked, setTucked] = useState(false);
  const [sleeves, setSleeves] = useState('regular'); // 'regular' | 'rolled'
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(true);
  const [looks, setLooks] = useState([]);
  const [lookName, setLookName] = useState('');
  const [savingLook, setSavingLook] = useState(false);
  const [adding, setAdding] = useState(false);
  const [manage, setManage] = useState(false);
  const [laundry, setLaundry] = useState(false);
  const [laundryPicks, setLaundryPicks] = useState(new Set());
  const [laundryBusy, setLaundryBusy] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [list, setList] = useState(items);
  const dragFrom = useRef(null);
  useEffect(() => { setList(items); }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  const onDragStart = ({ active }) => {
    const it = list.find((i) => i.id === active.id);
    dragFrom.current = it ? (it.category || 'other') : null;
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const overCat = over.data.current?.cat;
    const it = list.find((i) => i.id === active.id);
    if (!it || !overCat || (it.category || 'other') === overCat) return;
    setList((prev) => prev.map((i) => (i.id === active.id ? { ...i, category: overCat } : i)));
  };

  const onDragEnd = ({ active, over }) => {
    const it = list.find((i) => i.id === active.id);
    if (!it) return;
    const cat = it.category || 'other';
    const catIds = list.filter((i) => (i.category || 'other') === cat).map((i) => i.id);
    const from = catIds.indexOf(active.id);
    let to = over && over.id !== active.id ? catIds.indexOf(over.id) : from;
    if (to === -1) to = from;
    if (from === to && dragFrom.current === cat) return;
    const ordered = from !== to ? arrayMove(catIds, from, to) : catIds;
    setList((prev) => {
      const rest = prev.filter((i) => (i.category || 'other') !== cat);
      const moved = ordered.map((id) => prev.find((i) => i.id === id));
      return [...rest, ...moved];
    });
    nexaApi.post('/wardrobe/reorder', { category: cat, ordered_ids: ordered })
      .then(() => { if (dragFrom.current !== cat) toast.success('Moved'); })
      .catch(() => toast.error('Could not save the new order'));
  };

  useEffect(() => {
    nexaApi.get('/dressing/base')
      .then(({ data }) => setBasePhoto(data.photo_id))
      .catch(() => toast.error('Could not prepare the mannequin'))
      .finally(() => setPreparing(false));
    nexaApi.get('/looks').then(({ data }) => setLooks(data.looks)).catch(() => {});
  }, []);

  const isActive = (item) => {
    const v = selected[item.category || 'other'];
    return Array.isArray(v) ? v.includes(item.id) : v === item.id;
  };

  const toggle = (item) => {
    if (laundry) {
      setLaundryPicks((prev) => {
        const next = new Set(prev);
        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
        return next;
      });
      return;
    }
    if (manage) {
      setEditItem(item);
      return;
    }
    const cat = item.category || 'other';
    setSelected((prev) => {
      if (MULTI.has(cat)) {
        const list = prev[cat] || [];
        return {
          ...prev,
          [cat]: list.includes(item.id) ? list.filter((x) => x !== item.id) : [...list, item.id],
        };
      }
      return { ...prev, [cat]: prev[cat] === item.id ? undefined : item.id };
    });
    setPhoto(null);
  };

  const selectedIds = Object.values(selected).flat().filter(Boolean);
  const hasTop = Boolean(selected.top);
  const hasBottom = Boolean(selected.bottom);

  const dress = async () => {
    if (!selectedIds.length || busy) return;
    setBusy(true);
    try {
      const { data } = await nexaApi.post('/dressing', {
        item_ids: selectedIds,
        tucked: hasTop && hasBottom ? tucked : null,
        sleeves: hasTop ? sleeves : null,
      });
      setPhoto(data.photo_id);
    } catch {
      toast.error('Could not dress the mannequin — try again');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSelected({});
    setPhoto(null);
  };

  const saveLook = async () => {
    const name = lookName.trim();
    if (!name || !selectedIds.length || savingLook) return;
    setSavingLook(true);
    try {
      const { data } = await nexaApi.post('/looks', {
        name, item_ids: selectedIds,
        tucked: hasTop && hasBottom ? tucked : null,
        sleeves: hasTop ? sleeves : null,
        photo_id: photo,
      });
      setLooks((prev) => [data, ...prev]);
      setLookName('');
      toast.success(`Look saved — ${name}`);
    } catch {
      toast.error('Could not save the look');
    } finally {
      setSavingLook(false);
    }
  };

  const enterLaundry = () => {
    setLaundry(true); setLaundryPicks(new Set()); setManage(false);
  };
  const exitLaundry = () => {
    setLaundry(false); setLaundryPicks(new Set());
  };
  const commitLaundry = async (atCleaning) => {
    const ids = Array.from(laundryPicks);
    if (!ids.length) return;
    setLaundryBusy(true);
    try {
      await nexaApi.post('/wardrobe/cleaning/bulk',
        { item_ids: ids, at_cleaning: atCleaning });
      toast.success(atCleaning
        ? `Sent ${ids.length} to cleaning`
        : `${ids.length} back from cleaning`);
      onReload();
      exitLaundry();
    } catch {
      toast.error('Could not update');
    } finally {
      setLaundryBusy(false);
    }
  };


  const wearLook = (look) => {
    const byId = Object.fromEntries(items.map((i) => [i.id, i]));
    const next = {};
    look.item_ids.forEach((id) => {
      const it = byId[id];
      if (!it) return;
      const cat = it.category || 'other';
      if (MULTI.has(cat)) next[cat] = [...(next[cat] || []), id];
      else next[cat] = id;
    });
    setSelected(next);
    setTucked(Boolean(look.tucked));
    setSleeves(look.sleeves === 'rolled' ? 'rolled' : 'regular');
    setPhoto(look.photo_id || null);
  };

  const deleteLook = async (look) => {
    setLooks((prev) => prev.filter((l) => l.id !== look.id));
    try {
      await nexaApi.delete(`/looks/${look.id}`);
    } catch {
      toast.error('Could not delete');
      nexaApi.get('/looks').then(({ data }) => setLooks(data.looks));
    }
  };

  const shown = photo || basePhoto;
  const grouped = CATS
    .map((c) => ({ ...c, list: list.filter((i) => (i.category || 'other') === c.id) }))
    .filter((g) => manage || g.list.length > 0);

  return (
    <div data-testid="nexa-dressing-room">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          Dressing room
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => (laundry ? exitLaundry() : enterLaundry())}
            data-testid="nexa-laundry-toggle"
            className={`flex items-center gap-1 h-8 px-3 rounded-lg border text-[11px] font-medium active:scale-95 transition-all ${
              laundry ? 'border-sky-400 text-sky-300 bg-sky-400/10' : 'border-white/15 text-white/50'
            }`}
          >
            <WashingMachine className="w-3 h-3" strokeWidth={2} />
            {laundry ? 'Done' : 'Laundry'}
          </button>
          <button
            onClick={() => setManage((m) => !m)}
            data-testid="nexa-manage-toggle"
            className={`flex items-center gap-1 h-8 px-3 rounded-lg border text-[11px] font-medium active:scale-95 transition-all ${
              manage ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-white/15 text-white/50'
            }`}
          >
            <Pencil className="w-3 h-3" strokeWidth={2.5} />
            {manage ? 'Done' : 'Edit'}
          </button>
          <button
            onClick={() => setAdding(true)}
            data-testid="nexa-wardrobe-add-btn"
            className="flex items-center gap-1 h-8 px-3 rounded-lg border border-sky-400/50 text-sky-400 text-[11px] font-medium active:scale-95 transition-transform"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            Add item
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
        <div className="relative w-full aspect-[3/4] bg-[#101010]">
          {shown && !busy && (
            <img src={photoUrl(shown)} alt="Mannequin" data-testid="nexa-mannequin-image"
              className="absolute inset-0 w-full h-full object-cover" />
          )}
          {(preparing || busy) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 backdrop-blur-sm"
              data-testid="nexa-dressing-loading">
              <div className="w-6 h-6 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
              <p className="text-[11px] font-mono tracking-widest text-sky-400/80">
                {busy ? 'DRESSING MANNEQUIN…' : 'PREPARING MANNEQUIN…'}
              </p>
            </div>
          )}
        </div>

        <div className="p-4 flex flex-col gap-4">
          {manage && (
            <p className="text-[11px] text-amber-400/80 text-center -mb-1" data-testid="nexa-manage-hint">
              Edit mode — tap to rename or delete · hold &amp; drag to move items
            </p>
          )}
          {laundry && (
            <div data-testid="nexa-laundry-bar" className="rounded-xl border border-sky-400/35 bg-sky-400/[0.06] p-3 flex flex-col gap-2">
              <p className="text-[11px] text-sky-300 text-center">
                Laundry mode — tap items to select ({laundryPicks.size})
              </p>
              {laundryPicks.size > 0 && (() => {
                const picked = list.filter((i) => laundryPicks.has(i.id));
                const toSend = picked.filter((i) => !i.at_cleaning).length;
                const toReturn = picked.filter((i) => i.at_cleaning).length;
                return (
                  <div className="flex gap-2">
                    <motion.button whileTap={{ scale: 0.96 }}
                      onClick={() => commitLaundry(true)}
                      disabled={laundryBusy || toSend === 0}
                      data-testid="nexa-laundry-send"
                      className="flex-1 h-10 rounded-xl bg-sky-400 text-black font-semibold text-[12px] disabled:opacity-30 flex items-center justify-center gap-1.5"
                    >
                      <WashingMachine className="w-3.5 h-3.5" strokeWidth={2} />
                      Send {toSend > 0 ? `${toSend} ` : ''}to cleaning
                    </motion.button>
                    <motion.button whileTap={{ scale: 0.96 }}
                      onClick={() => commitLaundry(false)}
                      disabled={laundryBusy || toReturn === 0}
                      data-testid="nexa-laundry-back"
                      className="flex-1 h-10 rounded-xl border border-emerald-400/50 text-emerald-300 font-medium text-[12px] disabled:opacity-30 flex items-center justify-center gap-1.5"
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                      {toReturn > 0 ? `${toReturn} back` : 'Back from cleaning'}
                    </motion.button>
                  </div>
                );
              })()}
            </div>
          )}
          {hasTop && hasBottom && !manage && !laundry && (
            <div className="flex gap-2" data-testid="nexa-tuck-toggle">
              {[{ v: true, l: 'Tucked in' }, { v: false, l: 'Over the pants' }].map(({ v, l }) => (
                <button key={l} onClick={() => { setTucked(v); setPhoto(null); }}
                  data-testid={`nexa-tuck-${v ? 'in' : 'out'}`}
                  className={`flex-1 h-10 rounded-xl text-[12px] font-medium border transition-colors ${
                    tucked === v ? 'bg-sky-400 text-black border-sky-400' : 'border-white/15 text-white/60'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {hasTop && !manage && !laundry && (
            <div className="flex gap-2" data-testid="nexa-sleeves-toggle">
              {[{ v: 'regular', l: 'Regular sleeves' }, { v: 'rolled', l: 'Rolled up' }].map(({ v, l }) => (
                <button key={v} onClick={() => { setSleeves(v); setPhoto(null); }}
                  data-testid={`nexa-sleeves-${v}`}
                  className={`flex-1 h-10 rounded-xl text-[12px] font-medium border transition-colors ${
                    sleeves === v ? 'bg-sky-400 text-black border-sky-400' : 'border-white/15 text-white/60'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
            {grouped.map((g) => (
              <div key={g.id}>
                <div className="flex items-center gap-2 mb-2">
                  <g.icon className="w-3.5 h-3.5 text-white/40" strokeWidth={1.8} />
                  <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-white/35">
                    {g.label}{MULTI.has(g.id) && !manage ? ' · pick any' : ''}
                  </span>
                </div>
                <SortableContext items={g.list.map((i) => i.id)} strategy={rectSortingStrategy}>
                  {g.list.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {g.list.map((item) => (
                        <SortableTile key={item.id} item={item} manage={manage}
                          laundry={laundry}
                          laundryPicked={laundryPicks.has(item.id)}
                          active={isActive(item)} onTap={toggle} />
                      ))}
                    </div>
                  ) : (
                    <CatDropZone cat={g.id} />
                  )}
                </SortableContext>
              </div>
            ))}
          </DndContext>

          {grouped.length === 0 && (
            <p className="text-[13px] text-white/40 text-center py-2">
              Your inventory is empty — add items or send NEXA outfit photos in chat.
            </p>
          )}

          {!manage && !laundry && (
            <div className="flex gap-2">
              <motion.button whileTap={{ scale: 0.96 }} onClick={dress}
                disabled={!selectedIds.length || busy}
                data-testid="nexa-dress-btn"
                className="flex-1 h-12 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-30 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" strokeWidth={2} />
                Dress mannequin{selectedIds.length ? ` (${selectedIds.length})` : ''}
              </motion.button>
              <button onClick={reset} disabled={busy} data-testid="nexa-dress-reset" aria-label="Reset"
                className="w-12 h-12 rounded-xl border border-white/15 flex items-center justify-center text-white/60 active:scale-95 transition-transform">
                <RotateCcw className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          )}

          <AnimatePresence>
            {photo && selectedIds.length > 0 && !manage && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="flex gap-2">
                  <input value={lookName} onChange={(e) => setLookName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveLook()}
                    placeholder="Name this look — e.g. Friday dinner"
                    data-testid="nexa-look-name-input"
                    className="nexa-input flex-1 h-11 rounded-xl px-4 text-sm" />
                  <motion.button whileTap={{ scale: 0.95 }} onClick={saveLook}
                    disabled={!lookName.trim() || savingLook}
                    data-testid="nexa-look-save"
                    className="h-11 px-4 rounded-xl border border-sky-400/50 text-sky-400 font-medium text-sm disabled:opacity-30 flex items-center gap-1.5">
                    <Bookmark className="w-4 h-4" strokeWidth={2} />
                    Save
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {looks.length > 0 && (
        <div className="mt-5">
          <h3 className="text-[10px] font-mono tracking-[0.2em] uppercase text-white/35 mb-2 px-1">
            Saved looks · {looks.length}
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-1" data-testid="nexa-saved-looks">
            {looks.map((look) => (
              <div key={look.id} className="relative shrink-0 w-28">
                <button onClick={() => wearLook(look)} data-testid={`nexa-look-${look.id}`}
                  className="w-full rounded-2xl border border-white/10 bg-[#0A0A0A] overflow-hidden active:scale-95 transition-transform text-left">
                  {look.photo_id ? (
                    <img src={photoUrl(look.photo_id)} alt="" loading="lazy" className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-white/[0.04]">
                      <Shirt className="w-5 h-5 text-white/25" strokeWidth={1.5} />
                    </div>
                  )}
                  <p className="text-[11px] text-white/80 px-2.5 py-2 leading-tight line-clamp-2">{look.name}</p>
                </button>
                <button onClick={() => deleteLook(look)} data-testid={`nexa-look-delete-${look.id}`}
                  aria-label="Delete look"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/25 flex items-center justify-center text-white/70">
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {adding && <AddItemModal onClose={() => setAdding(false)} onAdded={onReload} />}
        {editItem && (
          <EditItemModal item={editItem} onClose={() => setEditItem(null)} onReload={onReload} />
        )}
      </AnimatePresence>
    </div>
  );
};
